/**
 * @file GameplayBaseComponent.ts
 * @description
 * Abstract Angular component that handles the core functionality for a gameplay interface, such as presenting questions, choices, and managing audio playback.
 * This component is designed to be extended by more specific components that define particular game logic and UI.
 */

import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  OnDestroy,
  Input,
  inject,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core'
import { BehaviorSubject, takeUntil, skipWhile, delay } from 'rxjs'

import { log, partition, isMandarin, convertAttributedText, toNonNullableArray } from '@nx-superprep/utils'

import {
  gameplayBaseStyling,
  gameplayReplace,
  gameplaySpacing,
  gameplayStyling,
} from '@nx-superprep/utils'

import {
  FrontendCard as Card,
  Choice,
  Playlist,
  GameplayName,
  GameplayWithQuestionTextHidden,
  GameplayWithAnswerTextHidden,
  StudySetting,
  StorageService,
} from '@nx-superprep/backend/firebase'

import { PaneType, PlayButtonActionType, ResponsiveComponent } from '@nx-superprep/shared'
import { isEmpty } from 'lodash-es'
import { GameplayLifecycle } from '../gameplay-lifecycle'

export {
  gameplayBaseStyling,
  gameplayReplace,
  gameplaySpacing,
  gameplayStyling,
} from '@nx-superprep/utils'

/**
 * @class GameplayBaseComponent
 * @description
 * Base component that handles the gameplay for presenting questions, audio, and choices.
 * This component manages UI state, user interactions, animations, and audio playback.
 */
@Component({
  standalone: true,
  selector: 'gameplay-base',
  template: 'THIS ABSTRACT CLASS HAS NO TEMPLATE',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export abstract class GameplayBaseComponent extends ResponsiveComponent implements OnInit, OnDestroy, GameplayLifecycle {
  readonly storageService = inject(StorageService)

  private _card: Card | undefined
  /**
   * The card containing gameplay data such as questions and choices.
   * @type {Card}
   */
  @Input()
  public get card(): Card | undefined {
    return this._card
  }
  public set card(value: Card | undefined) {
    this.resetState()
    this.initState(value)
  }
  private _didAnswer = false
  /**
   * Boolean flag indicating if the user has answered the current question.
   * @type {boolean}
   */
  @Input()
  public get didAnswer() {
    return this._didAnswer
  }
  public set didAnswer(value) {
    this._didAnswer = value
    this.cdr.markForCheck(); // If using OnPush strategy
  }

  /**
   * Settings for the gameplay, such as animation preferences.
   * @type {StudySetting}
   */
  @Input() setting!: StudySetting

    /**
   * Event emitted when a choice is selected.
   * @event
   * @type {EventEmitter<Choice>}
   */
  @Output() selected = new EventEmitter<Choice>()

  //--------------------------------------------------
  // Lifecycle Methods (Abstract)
  //--------------------------------------------------
  // Implement lifecycle methods (already enforced by GameplayBaseComponent)
  onPresentQuestion(): void {
    log.debug(`${this.title}: Presenting question...`);
  }

  onAnswerQuestion(choices: string[]): void {
    log.debug(`${this.title}: Answer selected:`, choices);
  }

  onPresentAnswer(): void {
    log.debug(`${this.title}: Revealing answer...`);
  }

  onFinishCard(): void {
    log.debug(`${this.title}: Finishing card...`);
  }
  /**
   * Force answers programmatically.
   * @param {boolean} [value=Math.random() < 0.5] - Randomly determines if the forced answer should be correct or incorrect.
   */
  onForceAnswer(value: boolean = Math.random() < 0.5) {
    this.onSelect(value ? this.answer : this.card?.template.choices.find((c) => !c.isAnswer))
  }

  /** The title of the gameplay, default is 'none'. */
  title = 'none'

  /** List of choices for the current question. */
  choices: Choice[] = []

  /** Audio playlist for the current card's question and choices. */
  playlist = new Playlist([])

  /** Animation state of the component. */
  animationState = false

  /** Tracks whether animations should happen with state transitions. */
  animationWithState = false

  /** Indicates if the animations are completed. */
  animationDone = false

  private _count = 0
  private _audioChoices: Choice[] = []
  private _chosen: Choice | undefined
  private _audioProgress = 0
  private _activePane: PaneType = 'left'
  private _isAudioPending = false;

  /** Stores audio elements associated with the current question. */
  get audioChoices() {
    return this._audioChoices
  }
  set audioChoices(value) {
    this._audioChoices = value
  }

   /** Whether audio is currently playing or not. */
  get isPlaying() {
    return this.playlist.isPlaying
  }
  set isPlaying(value) {
    if (value) {
      this.playlist.play()
    } else {
      this.playlist.stop()
      this._audioProgress = 0
    }
  }

  get audioReady() {
    return this.playlist.isLoaded
  }

  /** Tracks progress of audio playback. */
  get audioProgress() {
    return this._audioProgress
  }

  get audioDuration() {
    return this.playlist.duration
  }

  get didPlayAudio() {
    return this.audioChoices.length > 0
  }

  get isMobileView() {
    return this.isHandset
  }

  // UI Layout properties based on screen type
  get rowHeight(): string {
    return this.isMobileView ? '4rem' : '5rem'
  }

  get gutterSize(): string {
    return this.isMobileView ?  '8px' : '16px'
  }

  get headerContainerHeight() {
    return this.isMobileView && this.cols < 3 ? '130px' : '200px'
  }

  get cols(): number {
    return this.choices.length & 1 ? 3 : 2
  }

  get playButtonDiameter() {
    return this.isMobileView ? 40 : 50
  }

  // Getters

  get shouldShowTopText() {
    return false
  }

  get shouldShowTextContent() {
    return !this.isAudioOnly || !this.isVideoOnly
  }

  get shouldShowPlayButton() {
    return !this.didAnswer && this.isAudioQuestion && !this.shouldShowTranslation
  }

  get isTextContentVisible() {
    return !this.isVideoOnly || this.didAnswer ? !this.isAudioOnlyAnswer : !this.isAudioOnlyQuestion
  }

  get shouldShowTranslation() {
    return (this.didAnswer && this.hasTranslation) || this.question?.shouldShowTranslation
  }

  get shouldShowPronunciation() {
    return this.didAnswer && this.hasPronunciation
  }

  get hasTranslation() {
    return (this.question?.translation ?? '').length > 0
  }

  get hasPronunciation() {
    return (this.question?.reference.pronunciation?.[0] ?? '').length > 0;
  }

  constructor(private cdr: ChangeDetectorRef) { super() }

  override ngOnDestroy(): void {
    super.ngOnDestroy()
    this.playlist.clear()
  }

  override ngOnInit() {
    super.ngOnInit()
    this.getData()
  }

  /**
   * Fetches data required to initialize the component.
   * This method can be overridden by derived classes to add custom logic
   */
  protected getData() { return }

  /**
   * Handles audio playback logic based on user interaction.
   * @param {PlayButtonActionType} [audios] - Optional parameter to control audio playback.
   * @param {Playlist} [playlist] - Optional playlist to manage playback.
   */
  onPlayButtonTap(audios: PlayButtonActionType = this.audioChoices, playlist = this.playlist) {
    if (this.didAnswer || this.isPlaying) { return 0 }
    if (typeof audios === 'string') {
      if (audios === 'start' && playlist.length > 0) { this.isPlaying = true }
      if (audios === 'stop' && playlist.isPlaying) { this.isPlaying = false }
    } else {
      this.isPlaying = false
      audios = toNonNullableArray(audios).filter((a) => a.hasAudio)
      if (audios.length === 0) { return 0 }
      playlist.load(audios)
      this.isPlaying = true
      log.debug(`audio ${this.chosen ? 'answer' : 'question'}`, playlist)
    }
    return playlist.duration
  }

  // This method can be overridden by derived classes to add custom logic
  protected afterSelect(choice: Choice | undefined): void { return }

  /**
   * Selects a choice when the user taps or clicks on it.
   * @param {Choice} [choice] - The choice selected by the user.
   */
  onSelect(choice: Choice | undefined): void {
    if (this.didAnswer) return;

    this._stopAudioAndResetState();
    if (!choice) {
      this.resetState();
      return;
    }

    this._handleChoiceSelection(choice);
    this._playPostAnswerAudioIfNeeded(choice);
    this.selected.emit(choice);
    // Call the hook for custom logic
    this.afterSelect(choice);
  }

  private _stopAudioAndResetState(): void {
    if (this._isAudioPending || this.isPlaying) this.playlist.stop();
    this._isAudioPending = true;
    this.animationState = false;
  }

  private _handleChoiceSelection(choice: Choice): void {
    this.chosen = choice;
    this.animate(choice);
  }

  private _playPostAnswerAudioIfNeeded(choice: Choice): void {
    const answer = this.answer;
    if (answer?.audio && (choice.isWrong || this.alwaysPlayPostAnswerAudios)) {
      this.storageService.getAudioDuration(answer.audio.name)
        .then((duration) => {
          if (answer && duration > 0) {
            this.playlist.load([answer]);
            this.playlist.play();
          }
        })
        .catch((error) => log.error('Audio load failed:', error))
        .finally(() => this._isAudioPending = false);
    }
  }

  /**
   * Handles answer button interaction by selecting the correct or incorrect answer.
   * @param {unknown} args - Event arguments.
   */
  onAnswerButtonTap(args: unknown) {
    const answer = args ? this.answer : this.card?.template.choices.find((c) => !c.isAnswer)
    this.onSelect(answer)
  }

  choiceLabel(args: [Choice, number]) {
    const choice = args[0]
    const index = args[1]
    switch (this.card?.template.gameplay?.name) {
      case GameplayName.Audio002:
      case GameplayName.Text003:
      case GameplayName.Image003:
        return (index + 1).toFixed(0)
      default: {
        const text = choice.textUrl
        const className = 'text'
        return this._convertAttributedText(text, className, `${text.length > 20 ? 'font-size:smaller' : ''}`)
      }
    }
  }

  questionText() {
    if (!this.card?.template.gameplay || !this.question || !this.answer) { return '' }
    const isHidden = GameplayWithQuestionTextHidden.includes(this.card.template.gameplay.name)
      || (this.answer.textUrl === this.question.textUrl && GameplayWithAnswerTextHidden.includes(this.card.template.gameplay.name))
    const text = this.question.textUrl ?? gameplaySpacing.spacer
    return isHidden
      ? gameplaySpacing.smallSpacer
      : this._convertAttributedText(!this.didAnswer ? text.replace(gameplayReplace.tag, gameplaySpacing.spacer) : text, 'text')
  }

  answerText() {
    const text = (isEmpty(this.answer?.textUrl) ? gameplaySpacing.spacer : this.answer?.textUrl)??''
    return this._convertAttributedText(text, 'text')
  }

  elementText() {
    if (!this.answer?.elementUrl) { return '' }
    return this._convertAttributedText(this.answer.elementUrl, 'text')
  }

  topText() {
    const text = this.question?.topText ?? ''
    if (isEmpty(text)) { return '' }
    const action = text.replace(/\s.*/, '')
    const object = text.replace(action, '')
    const t = action.length > 0
      ? '<p><strong>' + action.replace(gameplayReplace.tag,'$1') + '</strong>' + object + '</p>'
      : text
    return this._convertAttributedText(t, 'text')
  }

  bottomText() {
    return this._convertAttributedText(this.question?.bottomText ?? '', 'text')
  }

  endText() {
    const text = (!isEmpty(this.question?.endText) ? this.question?.endText : !isEmpty(this.question?.bottomText) ? this.question?.bottomText : '')??''
    const classTag = 'text'
    return this._convertAttributedText(text, classTag)
  }

  translation() {
    const text = this.question?.translation ?? ''
    const classTag = 'translation'
    return this._convertAttributedText(text, classTag)
  }

  pronunciation() {
    return this.question?.reference?.['pronunciation']
      ? this._convertAttributedText(
          (this.question.reference?.['pronunciation'] as Array<string>)[0],
          'pronunciation'.concat(isMandarin(this.question.name) ? ' zh_pinyin' : '')
        )
      : ''
  }

  showDetails(choice: unknown) {
    log.debug(choice)
  }

  get isAudioPlaying() {
    return this.isPlaying
  }

  public get chosen() {
    return this._chosen
  }
  public set chosen(value) {
    this._chosen = value
  }

  /** Active pane in the UI for layout and animations. */
  public get activePane() {
    return this._activePane
  }
  public set activePane(value) {
    this._activePane = value
  }

  isActive<T extends Choice>(choice: T) {
    return choice.isActive && (!this.didAnswer || !choice.isAnswer)
  }

  isChosen<T extends Choice>(choice: T) {
    return this.didAnswer && choice?.name === this.chosen?.name
  }

  isCorrect<T extends Choice>(choice?: T) {
    return this.didAnswer && (!choice || choice.isAnswer)
  }

  // card accessors

  get question() {
    return this.card?.template.question
  }

  get answer() {
    return this.card?.template.answer
  }

  get alwaysPlayPostAnswerAudios() {
    return !!this.card?.gameplay.alwaysPlayPostAnswerAudios
  }
  // iterable for @for
  get answers() {
    return [this.answer]
  }

  get isAudioQuestion() {
    return !!this.card?.template.isAudioQuestion
  }

  get isAudioOnlyQuestion() {
    return !!this.card?.template.isAudioOnlyQuestion
  }

  get isAudioOnlyAnswer() {
    return !!this.card?.template.isAudioOnlyAnswer
  }

  get isImageOnlyQuestion() {
    return !!this.card?.template.isImageOnlyQuestion
  }

  get isImageOnlyAnswer() {
    return !!this.card?.template.isImageOnlyAnswer
  }

  get isVideoOnlyQuestion() {
    return !!this.card?.template.isVideoOnlyQuestion
  }

  get isVideoOnlyAnswer() {
    return !!this.card?.template.isVideoOnlyAnswer
  }

  get isAudioOnly() {
    return !!this.card?.template.isAudioOnly
  }

  get isImageOnly() {
    return !!this.card?.template.isImageOnly
  }

  get isVideoOnly() {
    return !!this.card?.template.isVideoOnly
  }

  get isMediaOnly() {
    return !!this.card?.template.isAudioOnly
  }

  // animations

  get slideDuration() {
    return this.animated ? 300 : 0
  }

  get animated() {
    return this.setting?.animation
  }

  isAnimated(choice?: Choice) {
    return this.animated && choice?.animationState
  }

  /**
   * Animates a choice when selected, handling transitions and timing.
   * @param {Choice} choice - The choice to animate.
   */
  protected animate(choice: Choice) {
    this.animateChoice(choice);
    this.waitForAnimationDelay(choice).subscribe(() => this.animatePane(choice));
  }

  protected animateChoice(choice: Choice) {
    choice.isActive = !choice.isAnswer;
    choice.animationState = !choice.isAnswer;
    this.animationState = choice.animationState;
  }

  protected animatePane(choice: Choice) {
    this.activePane = choice.isAnswer ? 'left' : 'right';
  }

  private waitForAnimationDelay(choice: Choice) {
    return waitForDelay(this.isAnimated(choice) ? 800 : 0)
      .pipe(takeUntil(this.onDestroy));
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onAnimationDone(event: unknown) {
    if (++this._count >= this.choices.length) {
      this._count = 1
      this.animationDone = !this.animationDone
      // log.debug(event)
    }
  }

  protected initState(card?: Card | undefined) {
    if (this.card && card?.isSameName(this.card)) { return false }
    this._card = card
    if (!card) { return false }
    this.choices = this.choices.length > 0
      ? this.choices
      : shuffledChoices(
          card.template.choices.filter((c) => c.isAssetized),
          card.template.gameplay?.choiceCount ?? card.template.choices.length
        )
    this.choices.forEach((c) => (c.isActive = false, c.animationState = false))
    this.audioChoices = this.question?.hasAudio ? [this.question] : this.choices.filter((c) => c.isAudio)
    log.debug('show', this.question, this.choices, this.audioChoices)
    this.onPlayButtonTap()
    return card.template.gameplay?.type === this.title
  }

  /**
   * Resets the component's state when a new card is loaded.
   * @param {Card} [card] - The new card to reset the state with.
   */
  protected resetState(card?: Card | undefined) {
    if (this.isPlaying) { log.debug('playlist is active') }
    this._audioChoices = []
    this._activePane = 'left'
    this._count = 1
    this._card = card
    this.choices = []
    this.chosen = undefined
    this.didAnswer = false
    this.isPlaying = false
    this.animationDone = false
    this.animationState = false
    this.animationWithState = false
    this.playlist.clear()
  }

  /**
   * Converts attributed text into styled HTML based on the provided class and style tags.
   *
   * @param {string} text - The input text to be styled.
   * @param {string} classTag - The class tag used to determine the styling.
   * @param {string} [styleTag] - An optional style tag to apply additional styling.
   * @returns {string} - The styled HTML string.
   *
   * @example
   * const styledText = this._convertAttributedText('Example text', 'translation', 'custom-style');
   * log.debug(styledText); // Output: '<span style="...">Example text</span>'
   */
  protected _convertAttributedText(text: string, classTag: string, styleTag?: string): string {
    if (!text) return '';
    const choiceStyle = this._getChoiceStyle(classTag);
    return convertAttributedText({
      text: this._styleText(text, choiceStyle),
      classTag,
      styleTag
    });
  }

  /**
   * Determines the appropriate style for a given class tag.
   *
   * @param {string} classTag - The class tag used to determine the style.
   * @returns {string} - The style string based on the class tag.
   *
   * @example
   * const style = this._getChoiceStyle('translation');
   * log.debug(style); // Output: gameplayStyling.baseStyle
   */
  private _getChoiceStyle(classTag: string): string {
    return classTag.startsWith('translation')
      ? gameplayStyling.baseStyle
      : this.chosen
        ? gameplayStyling.answerStyle
        : gameplayStyling.questionStyle;
  }

  /**
   * Applies styling to the input text, including subscripts, superscripts, and other replacements.
   *
   * @param {string} text - The input text to be styled.
   * @param {string} style - The style string to apply.
   * @returns {string} - The styled HTML string.
   *
   * @example
   * const styledText = this._styleText('Example text', 'font-size: 1em;');
   * log.debug(styledText); // Output: '<span style="font-size: 1em;">Example text</span>'
   */
  private _styleText(text: string, style: string): string {
    return this._styleSubSup(text)
      .replace(gameplayReplace.tag, `<span style="${style}">$1</span>`)
      .replace(gameplayReplace.ell, `<strong style="${style}">${gameplayBaseStyling.ellipsis}</strong>`)
      .replace(gameplayReplace.cleanup, '');
  }

  /**
   * Splits text at semicolons and filters out empty strings.
   *
   * @param {string} text - The input text to be split.
   * @returns {string[]} - An array of non-empty strings split by semicolons.
   *
   * @example
   * const result = this._styleSemicolon('part1;part2;');
   * log.debug(result); // Output: ['part1', 'part2']
   */
  protected _styleSemicolon(text: string) {
    return text.split(';').filter(t => t.length > 0)
  }

  /**
   * Wraps text within parentheses with a comment style span.
   *
   * @param {string} text - The input text to be styled.
   * @returns {string} - The styled HTML string.
   *
   * @example
   * const styledText = this._styleParenthesis('Example (comment)');
   * log.debug(styledText); // Output: 'Example <span style="...">(comment)</span>'
   */
  protected _styleParenthesis(text: string) {
    return text.replace(gameplayReplace.parentheses, '<span style="' + gameplayBaseStyling.commentStyle + '">$1</span>')
  }

  /**
   * Applies subscript and superscript styling to the input text.
   *
   * @param {string} text - The input text to be styled.
   * @returns {string} - The styled HTML string.
   *
   * @example
   * const styledText = this._styleSubSup('Example H2O');
   * log.debug(styledText); // Output: 'Example H<sub>2</sub>O'
   */
  protected _styleSubSup(text: string): string {
    // First pass: Convert subscripts
    text = this._convertSubscripts(text);

    // Second pass: Convert superscripts
    text = this._convertSuperscripts(text);

    return text;
  }

  /**
   * Converts subscript notation in the text to HTML `<sub>` tags.
   *
   * @param {string} text - The input text containing subscript notation.
   * @returns {string} - The text with subscript notation replaced by `<sub>` tags.
   *
   * @example
   * const result = this._convertSubscripts('H_2O');
   * log.debug(result); // Output: 'H<sub>2</sub>O'
   */
  private _convertSubscripts(text: string): string {
    return text.replace(
      /([^\s_^])_(?:[{<"]([^}>"\s]+)[}>""]|(?:&nbsp;|&hairsp;)|([^\s{<"_^]))/g,
      (match, prevChar, bracketContent, singleChar) => {
        const content = bracketContent || singleChar || '&nbsp;';
        const effectiveLength = content === '&nbsp;' || content === '&hairsp;' ? 1 : content.length;
        return `${prevChar}<sub style="position: relative; font-size: .5em; line-height: 1.2;" data-length="${effectiveLength}">${content}</sub>`;
      }
    );
  }

  /**
   * Converts superscript notation in the text to HTML `<sup>` tags.
   *
   * @param {string} text - The input text containing superscript notation.
   * @returns {string} - The text with superscript notation replaced by `<sup>` tags.
   *
   * @example
   * const result = this._convertSuperscripts('E=mc^2');
   * log.debug(result); // Output: 'E=mc<sup>2</sup>'
   */
  private _convertSuperscripts(text: string): string {
    return text.replace(
      /([^\\^]*)(\^(?:(?:[{<"](?:[^}>"\s]+)[}>""]|(?:&nbsp;|&hairsp;)|(?:[^\s{<"_^]))(?:\^(?:[{<"](?:[^}>"\s]+)[}>""]|(?:&nbsp;|&hairsp;)|(?:[^\s{<"_^])))*))/g,
      (match, preceding, supersGroup) => {
        const leftOffset = this._calculateLeftOffset(preceding);
        const supContent = this._parseSupTokens(supersGroup);
        return `${preceding}<sup style="position: relative; font-size: .5em; line-height: 1.2; top: -1.2em; left: ${leftOffset};">${supContent}</sup>`;
      }
    );
  }

  /**
   * Calculates the left offset for superscript positioning based on preceding text.
   *
   * @param {string} precedingText - The text preceding the superscript.
   * @returns {string} - The calculated left offset in CSS units.
   *
   * @example
   * const offset = this._calculateLeftOffset('H<sub>2</sub>');
   * log.debug(offset); // Output: '-0.8em'
   */
  private _calculateLeftOffset(precedingText: string): string {
    const subTagMatch = precedingText.match(/<sub [^>]*>(.*?)<\/sub>/);
    if (!subTagMatch) return '-0.1em';

    const subContent = subTagMatch[1];
    const isPlaceholder = subContent === '&nbsp;' || subContent === '&hairsp;' || subContent.trim() === '';
    const effectiveLength = isPlaceholder ? 2 : subContent.length;
    return `${-0.5 - (effectiveLength - 1) * 0.3}em`;
  }

  /**
   * Parses superscript tokens from a string and joins them into a single string.
   *
   * @param {string} tokensStr - The string containing superscript tokens.
   * @returns {string} - The concatenated superscript content.
   *
   * @example
   * const result = this._parseSupTokens('^2^3');
   * log.debug(result); // Output: '23'
   */
  private _parseSupTokens(tokensStr: string): string {
    const tokenRegex = /\^(?:[{<"]([^}>"\s]+)[}>""]|(&nbsp;|&hairsp;)|([^\s{<"_^]))/g;
    const tokens: string[] = [];
    let match: RegExpExecArray | null;
    while ((match = tokenRegex.exec(tokensStr)) !== null) {
      tokens.push(match[1] || match[2] || match[3] || '&nbsp;');
    }
    return tokens.join('');
  }
}

/**
 * Return a randomized list of choices for the card, with only one being correct
 *
 * @param count the number of choices to return
 * @param validate only select choices that have valid assets
 */
const shuffledChoices = (choices: Choice[], count: number): Choice[] => {
  count = count > 0 ? count : 2
  const [answer, wrongs] = partition(choices, (c) => c.isAnswer)
  return [...answer, ...(wrongs.shuffled()).slice(0, count - 1)].shuffle()
}

/**
 * Plays an audio file from the provided URL and returns a promise that resolves when the audio ends.
 *
 * @param {string} url - The URL of the audio file to play.
 * @returns {Promise<void>} - A promise that resolves when the audio playback ends or rejects on error.
 *
 * @example
 * playAudioURL('https://example.com/audio.mp3')
 *   .then(() => log.debug('Audio playback finished'))
 *   .catch((error) => console.error('Audio playback failed:', error));
 */
export const playAudioURL = async (url: string) => await new Promise((resolve, reject) => {
  const audio = new Audio()
  audio.preload = 'auto'
  audio.autoplay = false
  audio.onerror = reject
  audio.onended = resolve
  audio.src = url
})

/**
 * Creates an observable that waits for a specified delay before emitting a value.
 *
 * @param {number} d - The delay in milliseconds.
 * @returns {Observable<boolean>} - An observable that emits `true` after the specified delay.
 *
 * @example
 * waitForDelay(1000).subscribe(() => log.debug('1 second has passed'));
 */
export const waitForDelay = (d: number) => (new BehaviorSubject(true)).pipe(skipWhile(b => !b), delay(d))

/**
 * Creates an observable that waits for the next question delay based on various factors,
 * such as correctness, animation, audio duration, and time already spent.
 *
 * @param {boolean} isCorrect - Indicates whether the current answer is correct.
 * @param {number} rightAnswerTime - The base delay (in seconds) for a correct answer.
 * @param {number} wrongAnswerTime - The base delay (in seconds) for an incorrect answer.
 * @param {boolean} animation - Indicates whether animations are enabled.
 * @param {number} difficulty - The difficulty level of the question (currently unused in the calculation).
 * @param {number} [audioAnswerDuration=0] - The duration (in seconds) of the audio answer, if any.
 * @param {number} [spentDuration=0] - The time (in seconds) already spent on the current question.
 * @param {Playlist} playlist - The playlist object containing an `isDone` observable.
 * @returns {Observable<boolean>} - An observable that emits `true` after the calculated delay.
 *
 * @example
 * nextQuestion(true, 1.5, 2.5, true, 3, 1.2, 0.5, playlist)
 *   .subscribe(() => log.debug('Next question ready'));
 */
export const nextQuestion = (
  isCorrect: boolean,
  rightAnswerTime: number,
  wrongAnswerTime: number,
  animation: boolean,
  difficulty: number,
  audioAnswerDuration = 0,
  spentDuration = 0,
  playlist: Playlist
) => (playlist?.isDone ?? new BehaviorSubject(true)).pipe(
  skipWhile(b => !b),
  delay(nextQuestionDelay(isCorrect, rightAnswerTime, wrongAnswerTime, animation, difficulty, audioAnswerDuration, spentDuration))
)

/**
 * Calculates the delay before the next question based on various factors such as correctness,
 * animation duration, audio duration, and time already spent.
 *
 * @param {boolean} isCorrect - Indicates whether the current answer is correct.
 * @param {number} rightAnswerTime - The base delay (in seconds) for a correct answer.
 * @param {number} wrongAnswerTime - The base delay (in seconds) for an incorrect answer.
 * @param {boolean} animation - Indicates whether animations are enabled.
 * @param {number} difficulty - The difficulty level of the question (currently unused in the calculation).
 * @param {number} [audioAnswerDuration=0] - The duration (in seconds) of the audio answer, if any.
 * @param {number} [spentDuration=0] - The time (in seconds) already spent on the current question.
 * @returns {number} - The delay (in milliseconds) before the next question.
 *
 * @example
 * const delay = nextQuestionDelay(true, 1.5, 2.5, true, 3, 1.2, 0.5);
 * log.debug(delay); // Output: 1200 (1.2 seconds)
 */
export const nextQuestionDelay = (
  isCorrect: boolean,
  rightAnswerTime: number,
  wrongAnswerTime: number,
  animation: boolean,
  difficulty: number,
  audioAnswerDuration = 0,
  spentDuration = 0,
): number => {
  // max delay depending on correctness of answer
  const _maxDelay = (isCorrect ? 0.4 + rightAnswerTime : wrongAnswerTime)
  // animation
  const _animationDuration =
    //0.8 *
    (animation
      ? isCorrect
        ? 0.15 // Fade out
        : 0.2 + (audioAnswerDuration > 0 ? 0.2 : 0) + 0.15 // Shake + present + fade out
      : 0)
  // audio
  const _audioDuration = audioAnswerDuration
  // delay in seconds
  const _delay = 0.1 + Math.max(_maxDelay, _animationDuration + _audioDuration, spentDuration) - spentDuration
  log.debug('delay:', _delay.toFixed(3), 'max: ' + _maxDelay.toFixed(3), 'spent: ' + spentDuration, _audioDuration ? 'audio: ' + _audioDuration.toFixed(3) : '')
  // return milliseconds value
  return _delay * 1000
}
